/**
 * Agency Welcome Center
 * 
 * "Welcome to Leynix. Let's get your agency operational."
 * 
 * This is the first screen agency owners/admins see after login.
 * It shows setup progress, go-live readiness, and next recommended steps.
 * 
 * After setup is complete, users are directed to their role-based portal.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, Circle, ArrowRight, Building2, Users, UserPlus,
  Calendar, DollarSign, FileText, Settings, Sparkles, ChevronRight,
  Clock, AlertCircle, TrendingUp, Shield, Heart, Rocket
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { useAgency } from '../contexts/AgencyContext';
import api from '../lib/api';

// Setup steps configuration - Go-Live Workflow
// 1. Complete Agency Profile
// 2. Add Office Staff
// 3. Add Clients  
// 4. Add Caregivers
// 5. Create Client Assignments
// 6. Begin Scheduling
// 7. Go Live!
const SETUP_STEPS = [
  {
    id: 'agency_profile',
    title: 'Complete Organization Profile',
    description: 'Add your organization name, address, contact info, and branding',
    path: '/settings',
    icon: Building2,
    requiredFields: ['name', 'phone', 'address'],
  },
  {
    id: 'add_employees',
    title: 'Add Your Team',
    description: 'Add office staff and team members',
    path: '/employees',
    icon: Users,
    minCount: 1,
    entity: 'employees',
  },
  {
    id: 'add_clients',
    title: 'Add Clients',
    description: 'Import or create your first client records',
    path: '/clients',
    icon: Heart,
    minCount: 1,
    entity: 'clients',
  },
  {
    id: 'create_assignments',
    title: 'Create Client Assignments',
    description: 'Assign team members to clients (Primary, Secondary, Backup)',
    path: '/care-team',
    icon: UserPlus,
    minCount: 1,
    entity: 'client_caregiver_relationships',
  },
  {
    id: 'schedule_visit',
    title: 'Schedule First Visit',
    description: 'Create your first scheduled visit',
    path: '/schedule-care',
    icon: Calendar,
    minCount: 1,
    entity: 'visits',
  },
  {
    id: 'configure_payroll',
    title: 'Configure Payroll',
    description: 'Set up pay rates, overtime rules, and pay periods',
    path: '/payroll',
    icon: DollarSign,
    optional: true,
  },
  {
    id: 'configure_billing',
    title: 'Configure Billing',
    description: 'Set up bill rates, payers, and billing preferences',
    path: '/billing',
    icon: FileText,
    optional: true,
  },
];

// Quick action cards for after setup
const QUICK_ACTIONS = [
  {
    title: 'Schedule Care',
    description: 'Create a new visit in under 30 seconds',
    path: '/schedule-care',
    icon: Calendar,
    color: 'bg-blue-500',
  },
  {
    title: 'Add Client',
    description: 'Start a new client intake',
    path: '/clients',
    icon: Heart,
    color: 'bg-green-500',
  },
  {
    title: 'Hiring',
    description: 'Review applications and hire caregivers',
    path: '/hiring',
    icon: UserPlus,
    color: 'bg-purple-500',
  },
  {
    title: 'Ask Leynix',
    description: 'Get answers about your agency',
    path: '/ask-aligned',
    icon: Sparkles,
    color: 'bg-amber-500',
  },
];

export default function AgencyWelcomeCenter() {
  const { user } = useAuth();
  const { agency } = useAgency();
  const navigate = useNavigate();
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    employees: 0,
    clients: 0,
    visits: 0,
    pendingApprovals: 0,
  });

  // Calculate setup progress
  const fetchSetupStatus = useCallback(async () => {
    try {
      // Fetch counts for various entities
      const [employeesRes, clientsRes, visitsRes, assignmentsRes] = await Promise.all([
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/clients').catch(() => ({ data: [] })),
        api.get('/scheduling/visits').catch(() => ({ data: [] })),
        api.get('/client-assignments/dashboard').catch(() => ({ data: { client_metrics: {} } })),
      ]);

      const employeeCount = Array.isArray(employeesRes.data) ? employeesRes.data.length : 
                           (employeesRes.data?.employees?.length || 0);
      const clientCount = Array.isArray(clientsRes.data) ? clientsRes.data.length : 
                         (clientsRes.data?.clients?.length || 0);
      const visitCount = Array.isArray(visitsRes.data) ? visitsRes.data.length : 
                        (visitsRes.data?.visits?.length || 0);
      const assignmentsCount = assignmentsRes.data?.client_metrics?.clients_assigned || 0;

      setStats({
        employees: employeeCount,
        clients: clientCount,
        visits: visitCount,
        assignments: assignmentsCount,
        pendingApprovals: 0,
      });

      // Calculate which steps are complete
      const completedSteps = [];
      
      // Agency profile complete?
      if (agency?.name && agency?.phone) {
        completedSteps.push('agency_profile');
      }
      
      // Has employees?
      if (employeeCount > 0) {
        completedSteps.push('add_employees');
      }
      
      // Has clients?
      if (clientCount > 0) {
        completedSteps.push('add_clients');
      }
      
      // Has client-caregiver assignments?
      if (assignmentsCount > 0) {
        completedSteps.push('create_assignments');
      }
      
      // Has visits?
      if (visitCount > 0) {
        completedSteps.push('schedule_visit');
      }
      
      // Payroll and billing are optional, mark as complete if agency has settings
      if (agency?.payroll_cycle) {
        completedSteps.push('configure_payroll');
      }
      
      if (agency?.billing_settings) {
        completedSteps.push('configure_billing');
      }

      setSetupStatus({
        completedSteps,
        totalSteps: SETUP_STEPS.filter(s => !s.optional).length,
        completedCount: completedSteps.filter(id => 
          !SETUP_STEPS.find(s => s.id === id)?.optional
        ).length,
      });

    } catch (error) {
      console.error('Error fetching setup status:', error);
    } finally {
      setLoading(false);
    }
  }, [agency]);

  useEffect(() => {
    fetchSetupStatus();
  }, [fetchSetupStatus]);

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.name?.split(' ')[0] || 'there';
  const progress = setupStatus 
    ? Math.round((setupStatus.completedCount / setupStatus.totalSteps) * 100) 
    : 0;
  const isSetupComplete = progress === 100;

  // Find next incomplete step
  const nextStep = SETUP_STEPS.find(step => 
    !step.optional && !setupStatus?.completedSteps?.includes(step.id)
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 w-64 bg-muted rounded" />
          <div className="h-32 bg-muted rounded-xl" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-48 bg-muted rounded-xl" />
            <div className="h-48 bg-muted rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8" data-testid="agency-welcome-center">
      {/* Header */}
      <header className="mb-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground" data-testid="welcome-title">
              {getGreeting()}, {firstName}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isSetupComplete 
                ? `Welcome back to ${agency?.name || 'Leynix'}`
                : 'Welcome to Leynix. Let\'s get your organization operational.'
              }
            </p>
          </div>
          {isSetupComplete && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/ops')}>
                <TrendingUp className="h-4 w-4 mr-2" />
                Operations
              </Button>
              <Button onClick={() => navigate('/schedule-care')}>
                <Calendar className="h-4 w-4 mr-2" />
                Schedule Care
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Setup Progress Card - Show if not complete */}
      {!isSetupComplete && (
        <Card className="mb-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="setup-progress-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Rocket className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Go-Live Readiness</CardTitle>
                  <CardDescription>
                    {setupStatus?.completedCount || 0} of {setupStatus?.totalSteps || 4} required steps complete
                  </CardDescription>
                </div>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-primary">{progress}%</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={progress} className="h-2 mb-6" />
            
            {/* Setup Steps */}
            <div className="space-y-3">
              {SETUP_STEPS.filter(s => !s.optional).map((step) => {
                const isComplete = setupStatus?.completedSteps?.includes(step.id);
                const Icon = step.icon;
                const isNext = step.id === nextStep?.id;
                
                return (
                  <div
                    key={step.id}
                    className={`flex items-center gap-4 p-3 rounded-lg border transition-all ${
                      isComplete 
                        ? 'bg-green-50 border-green-200' 
                        : isNext 
                          ? 'bg-primary/5 border-primary/30' 
                          : 'bg-muted/50 border-border'
                    }`}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                      isComplete ? 'bg-green-500 text-white' : 'bg-muted'
                    }`}>
                      {isComplete ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm ${isComplete ? 'text-green-700' : ''}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {step.description}
                      </p>
                    </div>
                    {!isComplete && (
                      <Button
                        size="sm"
                        variant={isNext ? 'default' : 'ghost'}
                        onClick={() => navigate(step.path)}
                        data-testid={`setup-step-${step.id}`}
                      >
                        {isNext ? 'Continue' : 'Start'}
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                    {isComplete && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Complete
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Next Step Highlight */}
            {nextStep && (
              <div className="mt-6 p-4 rounded-xl bg-primary text-primary-foreground">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium opacity-90">Recommended Next Step</p>
                    <p className="text-lg font-bold">{nextStep.title}</p>
                  </div>
                  <Button 
                    variant="secondary" 
                    onClick={() => navigate(nextStep.path)}
                    data-testid="continue-setup-btn"
                  >
                    Continue Setup
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Overview - Show if setup is complete or has some data */}
      {(isSetupComplete || stats.employees > 0 || stats.clients > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            label="Team Members" 
            value={stats.employees} 
            icon={Users}
            href="/employees"
          />
          <StatCard 
            label="Active Clients" 
            value={stats.clients} 
            icon={Heart}
            href="/clients"
          />
          <StatCard 
            label="Scheduled Visits" 
            value={stats.visits} 
            icon={Calendar}
            href="/scheduling"
          />
          <StatCard 
            label="Pending Approvals" 
            value={stats.pendingApprovals} 
            icon={Clock}
            href="/review"
            warning={stats.pendingApprovals > 0}
          />
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <Card 
                key={action.path}
                className="cursor-pointer hover:shadow-md transition-shadow group"
                onClick={() => navigate(action.path)}
              >
                <CardContent className="p-4">
                  <div className={`h-10 w-10 rounded-lg ${action.color} text-white flex items-center justify-center mb-3`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-medium text-sm group-hover:text-primary transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {action.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Getting Started Resources */}
      <Card className="border-dashed" data-testid="resources-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            Getting Started Resources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link 
              to="/data-migration" 
              className="p-3 rounded-lg border hover:bg-muted/50 transition-colors flex items-center gap-3"
            >
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Import Data</p>
                <p className="text-xs text-muted-foreground">Migrate from another system</p>
              </div>
              <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
            </Link>
            
            <Link 
              to="/settings" 
              className="p-3 rounded-lg border hover:bg-muted/50 transition-colors flex items-center gap-3"
            >
              <Settings className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Agency Settings</p>
                <p className="text-xs text-muted-foreground">Configure your agency</p>
              </div>
              <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
            </Link>
            
            <Link 
              to="/hiring" 
              className="p-3 rounded-lg border hover:bg-muted/50 transition-colors flex items-center gap-3"
            >
              <UserPlus className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Start Hiring</p>
                <p className="text-xs text-muted-foreground">Build your care team</p>
              </div>
              <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, icon: Icon, href, warning = false }) {
  return (
    <Link to={href} className="block">
      <Card className={`hover:shadow-md transition-shadow ${warning ? 'border-amber-200' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Icon className={`h-5 w-5 ${warning ? 'text-amber-500' : 'text-muted-foreground'}`} />
            {warning && value > 0 && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">
                Action needed
              </Badge>
            )}
          </div>
          <p className="text-2xl font-bold mt-2">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
